import type { FunctionComponent, PropsWithChildren } from "react";
import { createContext, useContext, useState } from "react";

import { useQueryLastUpdated } from "~/hooks/use-query-last-updated";

import type { QueryKey } from "@tanstack/react-query";

interface QueryUpdateContext {
  refetchRegisteredQueries: () => void;
  lastUpdated?: Date;
  registerQueryKeys: (queryKeys: QueryKey[]) => void;
  isRegisteredQueriesFetching: boolean;
  registeredQueriesError?: Error;
  registeredQueryKeys: QueryKey[];
}

const queryUpdateContext = createContext<QueryUpdateContext | null>(null);

export const QueryUpdateContextProvider: FunctionComponent<
  PropsWithChildren
> = ({ children }) => {
  const [queryKeys, setQueryKeys] = useState<QueryKey[]>([]);

  const { lastUpdated, error, isFetching, refetchPassedQueryKeys } =
    useQueryLastUpdated(queryKeys);

  return (
    <queryUpdateContext.Provider
      value={{
        refetchRegisteredQueries: refetchPassedQueryKeys,
        lastUpdated,
        registerQueryKeys: setQueryKeys,
        isRegisteredQueriesFetching: isFetching,
        registeredQueriesError: error,
        registeredQueryKeys: queryKeys,
      }}
    >
      {children}
    </queryUpdateContext.Provider>
  );
};

export const useQueryUpdateContext = () => {
  const context = useContext(queryUpdateContext);
  if (context) {
    return context;
  }
  throw new Error(
    "useQueryUpdateContext must be used within an QueryUpdateContextProvider",
  );
};
